import { useState, useRef, useCallback, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarColor } from '@/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface EmailChipInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  autoFocus?: boolean;
}

export function EmailChipInput({
  value,
  onChange,
  placeholder = 'destinatario@email.com',
  id,
  className,
  autoFocus,
}: EmailChipInputProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const addEmails = useCallback(
    (raw: string) => {
      const candidates = raw
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean);

      const newEmails = candidates.filter(
        (e) => !value.includes(e)
      );

      if (newEmails.length > 0) {
        onChange([...value, ...newEmails]);
      }
    },
    [value, onChange]
  );

  const commitInput = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      addEmails(trimmed);
      setInputValue('');
    }
  }, [inputValue, addEmails]);

  const removeEmail = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',') {
      if (inputValue.trim()) {
        e.preventDefault();
        commitInput();
      }
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeEmail(value.length - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted.includes(',') || pasted.includes(';') || pasted.includes(' ')) {
      e.preventDefault();
      addEmails(pasted);
    }
  };

  const isValid = (email: string) => EMAIL_REGEX.test(email);

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 min-h-[40px] w-full rounded-lg border bg-white px-3 py-1.5 transition-all duration-150',
          'border-gray-300 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20',
          className
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((email, index) => {
          const valid = isValid(email);
          const initial = email.charAt(0).toUpperCase();
          const avatarColor = getAvatarColor(email);

          return (
            <Tooltip key={`${email}-${index}`} delayDuration={300}>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-sm font-medium transition-colors',
                    valid
                      ? 'bg-primary/5 border border-primary/20 text-primary'
                      : 'bg-red-50 border border-red-300 text-red-700'
                  )}
                >
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white shrink-0"
                    style={{ backgroundColor: avatarColor }}
                  >
                    {initial}
                  </span>
                  <span className="truncate max-w-[180px]">{email}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeEmail(index);
                    }}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 transition-colors"
                    aria-label={`Remover ${email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{email}</TooltipContent>
            </Tooltip>
          );
        })}
        <input
          ref={inputRef}
          id={id}
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitInput}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          autoFocus={autoFocus}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
      </div>
    </TooltipProvider>
  );
}
